import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDocsFromServer, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { db } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { requestPortvilleContractCancellation } from '../../../services/PortvilleContractCancellationService';

interface ContractInfo {
  roomNumber: string;
  contractorInfo: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  occupancyPeriod: {
    startDate: Date;
    endDate: Date;
  };
  signatureName: string;
  signatureDate: Date | null;
  contractSignedAt: Date | null;
  status: 'drawn' | 'contracted' | 'history' | 'none';
  roomId?: string; // 계약서 조회를 위해 추가
  lotteryDocId?: string;
  cancellationRequestStatus?: 'pending' | 'rejected' | 'approved' | 'cancelled';
  cancellationReason?: string;
  canRequestCancellation?: boolean;
}

interface ContractContent {
  article1: string;
  article2: string;
  article3: string;
  article4: string;
  article5: string;
  signatureUrl?: string;
}

const isContractPageDebugEnabled = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('debug_portville_contract') === '1';
  } catch {
    return false;
  }
};

const debugLog = (...args: any[]) => {
  if (isContractPageDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log('[ContractPage]', ...args);
  }
};

const ContractPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();
  
  const [loading, setLoading] = useState(true);
  const [contractInfo, setContractInfo] = useState<ContractInfo | null>(null);
  const [historyContractInfo, setHistoryContractInfo] = useState<ContractInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsDocuments, setNeedsDocuments] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractContent, setContractContent] = useState<ContractContent | null>(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [cancellationError, setCancellationError] = useState<string | null>(null);
  const [isRequestingCancellation, setIsRequestingCancellation] = useState(false);

  const renderHistorySummary = (info: ContractInfo) => {
    const formatDate = (date: Date | null) => (date ? date.toLocaleDateString('ko-KR') : '-');
    const formatPeriod = (start?: Date, end?: Date) => {
      if (!start || !end) return '-';
      return `${start.toLocaleDateString('ko-KR')} ~ ${end.toLocaleDateString('ko-KR')}`;
    };

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">과거 계약 이력</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">호실번호</dt>
            <dd className="text-base font-semibold text-gray-900">{info.roomNumber ? `${info.roomNumber}호` : '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">계약일</dt>
            <dd className="text-base font-semibold text-gray-900">{formatDate(info.contractSignedAt)}</dd>
          </div>
          <div className="md:col-span-2">
            <dt className="text-sm font-medium text-gray-500">입주 기간</dt>
            <dd className="text-base font-semibold text-gray-900">
              {formatPeriod(info.occupancyPeriod?.startDate, info.occupancyPeriod?.endDate)}
            </dd>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading && !emailVerificationLoading) {
      loadContractInfo();
    }
  }, [user, authLoading, emailVerificationLoading]);

  useEffect(() => {
    if (isContractPageDebugEnabled()) {
      debugLog('Debug enabled. To disable: localStorage.removeItem("debug_portville_contract")');
    }
  }, []);

  const loadContractInfo = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      setNeedsDocuments(false);
      setHistoryContractInfo(null);
      debugLog('loadContractInfo start', { uid: user.uid, at: new Date().toISOString() });
      
      const getMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value.toMillis === 'function') return value.toMillis();
          if (typeof value.toDate === 'function') return value.toDate().getTime();
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? 0 : date.getTime();
        } catch {
          return 0;
        }
      };

      // 1. 새로운 데이터 구조에서 입주 기록 확인 (source-of-truth)
      // NOTE(client_residency.md): 계약/입주 상태의 기준은 portville_residencies를 우선한다.
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
      const residencySnapshot = await getDocs(residencyQuery);
      debugLog('residencies snapshot', {
        size: residencySnapshot.size,
        fromCache: residencySnapshot.metadata.fromCache
      });

      if (isContractPageDebugEnabled()) {
        try {
          const residencyServerSnapshot = await getDocsFromServer(residencyQuery);
          debugLog('residencies server snapshot', {
            size: residencyServerSnapshot.size,
            fromCache: residencyServerSnapshot.metadata.fromCache
          });
        } catch (serverErr) {
          debugLog('residencies server snapshot failed', serverErr);
        }
      }
      
      if (!residencySnapshot.empty) {
        // 입주 기록이 있는 경우 - 새로운 데이터 구조 사용
        // NOTE: 쿼리 결과 순서는 보장되지 않으므로, 명시적으로 정렬/우선순위를 둔다.
        const residencyDocs = residencySnapshot.docs.map((snap) => ({
          id: snap.id,
          ...snap.data()
        })) as Array<Record<string, any> & { id: string }>;

        debugLog(
          'residency candidates',
          residencySnapshot.docs.map((snap) => {
            const data: any = snap.data();
            return {
              id: snap.id,
              fromCache: snap.metadata.fromCache,
              status: data?.status,
              roomId: data?.roomId,
              roomNumber: data?.roomNumber,
              updatedAtMillis: getMillis(data?.updatedAt),
              completedAtMillis: getMillis(data?.completedAt),
              terminatedAtMillis: getMillis(data?.terminatedAt),
              contractSignedAtMillis: getMillis(data?.contractSignedAt),
              hasContractContent: Boolean(data?.contractContent)
            };
          })
        );

        const inProgressCandidates = residencyDocs
          .filter((r) => r.status === 'active' || r.status === 'contracting')
          .sort((a, b) => getMillis(b.updatedAt) - getMillis(a.updatedAt));

        // terminal anchor: updatedAt은 관리자 수정으로 흔들릴 수 있으므로 제외
        const terminalCandidates = residencyDocs
          .filter((r) => r.status === 'completed' || r.status === 'terminated')
          .sort((a, b) => {
            const aKey =
              getMillis(a.completedAt) ||
              getMillis(a.terminatedAt) ||
              getMillis(a.contractCancelledAt) ||
              getMillis(a.contractSignedAt);
            const bKey =
              getMillis(b.completedAt) ||
              getMillis(b.terminatedAt) ||
              getMillis(b.contractCancelledAt) ||
              getMillis(b.contractSignedAt);
            return bKey - aKey;
          });

        const inProgressResidency = inProgressCandidates[0] ?? null;
        const latestTerminalResidency = terminalCandidates[0] ?? null;
        const terminalAnchorMillis = terminalCandidates.reduce((acc, r) => {
          const key =
            getMillis((r as any).completedAt) ||
            getMillis((r as any).terminatedAt) ||
            getMillis((r as any).contractCancelledAt) ||
            getMillis((r as any).contractSignedAt);
          return Math.max(acc, key);
        }, 0);

        const computedHistoryContractInfo: ContractInfo | null = latestTerminalResidency
          ? {
            status: 'history',
            roomNumber: (latestTerminalResidency as any).roomNumber || '',
            contractorInfo: {
              name: (latestTerminalResidency as any).residentInfo?.name || '',
              phoneNumber: (latestTerminalResidency as any).residentInfo?.phoneNumber || '',
              organization: (latestTerminalResidency as any).residentInfo?.organization || ''
            },
            occupancyPeriod: {
              startDate: (latestTerminalResidency as any).occupancyPeriod?.startDate?.toDate?.() || new Date(),
              endDate: (latestTerminalResidency as any).occupancyPeriod?.endDate?.toDate?.() || new Date()
            },
            signatureName: (latestTerminalResidency as any).signatureName || '',
            signatureDate: (latestTerminalResidency as any).signatureDate?.toDate?.() || null,
            contractSignedAt: (latestTerminalResidency as any).contractSignedAt?.toDate?.() || null,
            roomId: (latestTerminalResidency as any).roomId
          }
          : null;

        if (computedHistoryContractInfo) {
          setHistoryContractInfo(computedHistoryContractInfo);
        }

        if (inProgressResidency) {
          debugLog('selected residency (in-progress)', {
            id: (inProgressResidency as any)?.id,
            status: (inProgressResidency as any)?.status,
            roomId: (inProgressResidency as any)?.roomId,
            updatedAtMillis: getMillis((inProgressResidency as any)?.updatedAt),
            contractSignedAtMillis: getMillis((inProgressResidency as any)?.contractSignedAt)
          });

	          if ((inProgressResidency as any).status === 'contracting' && !(inProgressResidency as any).contractSignedAt) {
	            // 계약 진행중이지만 아직 계약 완료되지 않은 경우
	            const lotteryRef = collection(db, 'portville_lottery_history');
	            const lotterySnapshot = await getDocs(query(lotteryRef, where('uid', '==', user.uid)));
	            const linkedLottery = lotterySnapshot.docs
	              .map((lotteryDoc) => ({ id: lotteryDoc.id, data: lotteryDoc.data() as any }))
	              .filter(({ data }) => (
	                data?.status === 'drawn' &&
	                !data?.archivedAt &&
	                String(data?.roomId || '') === String((inProgressResidency as any).roomId || '')
	              ))
	              .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt))[0];

	            setContractInfo({
	              status: 'drawn',
	              roomNumber: (inProgressResidency as any).roomNumber || '',
	              contractorInfo: {
	                name: (inProgressResidency as any).residentInfo?.name || '',
	                phoneNumber: (inProgressResidency as any).residentInfo?.phoneNumber || '',
	                organization: (inProgressResidency as any).residentInfo?.organization || ''
	              },
	              occupancyPeriod: {
	                startDate: (inProgressResidency as any).occupancyPeriod?.startDate?.toDate?.() || new Date(),
	                endDate: (inProgressResidency as any).occupancyPeriod?.endDate?.toDate?.() || new Date()
	              },
	              signatureName: '',
	              signatureDate: null,
	              contractSignedAt: null,
	              roomId: (inProgressResidency as any).roomId,
	              lotteryDocId: linkedLottery?.id,
	              cancellationRequestStatus: linkedLottery?.data?.cancellationRequestStatus,
	              cancellationReason: linkedLottery?.data?.cancellationReason,
	              canRequestCancellation: Boolean(
	                linkedLottery && linkedLottery.data?.cancellationRequestStatus !== 'pending'
	              )
	            });
	            return;
	          }

          // 계약이 완료(또는 입주중)인 경우 - 오프라인 계약(추첨 없이 생성된 active) 포함
          setContractInfo({
            status: 'contracted',
            roomNumber: (inProgressResidency as any).roomNumber || '',
            contractorInfo: {
              name: (inProgressResidency as any).residentInfo?.name || '',
              phoneNumber: (inProgressResidency as any).residentInfo?.phoneNumber || '',
              organization: (inProgressResidency as any).residentInfo?.organization || ''
            },
            occupancyPeriod: {
              startDate: (inProgressResidency as any).occupancyPeriod?.startDate?.toDate?.() || new Date(),
              endDate: (inProgressResidency as any).occupancyPeriod?.endDate?.toDate?.() || new Date()
            },
            signatureName: (inProgressResidency as any).signatureName || '',
            signatureDate: (inProgressResidency as any).signatureDate?.toDate?.() || null,
            contractSignedAt: (inProgressResidency as any).contractSignedAt?.toDate?.() || null,
            roomId: (inProgressResidency as any).roomId
          });
          return;
        }

        // terminal-only: 새 회차 lottery(추첨 완료/계약 전)가 있으면 그걸 메인으로 표시한다.
        if (latestTerminalResidency) {
          debugLog('terminal-only residencies detected', { terminalAnchorMillis });
          const lotteryRef = collection(db, 'portville_lottery_history');
          const lotteryQuery = query(lotteryRef, where('uid', '==', user.uid));
          const lotterySnapshot = await getDocs(lotteryQuery);
          debugLog('lottery snapshot (terminal-only)', {
            size: lotterySnapshot.size,
            fromCache: lotterySnapshot.metadata.fromCache
          });

          if (!lotterySnapshot.empty) {
            const lotteryDocs = lotterySnapshot.docs
              .map((d) => ({ id: d.id, data: d.data() as any }))
              .filter((d) => {
                const data = d.data;
                if (data?.status !== 'drawn') return false;
                if (data?.archivedAt) return false;
                if (data?.suppressedAt) return false;
                const drawnAtMillis = getMillis(data?.drawnAt);
                return drawnAtMillis > terminalAnchorMillis;
              })
              .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

            const latestDrawn = lotteryDocs[0]?.data ?? null;
            debugLog('selected lottery (terminal-only)', {
              id: lotteryDocs[0]?.id,
              status: latestDrawn?.status,
              roomId: latestDrawn?.roomId,
              roomNumber: latestDrawn?.roomNumber,
              drawnAtMillis: getMillis(latestDrawn?.drawnAt)
            });

            if (latestDrawn?.roomId) {
              const lotteryDocuments = (latestDrawn?.documents || {}) as Record<string, string | undefined>;
              const hasRequiredDocuments = Boolean(lotteryDocuments.identification && lotteryDocuments.employment);
              if (!hasRequiredDocuments) {
                setNeedsDocuments(true);
              }
              setContractInfo({
                status: 'drawn',
                roomNumber: latestDrawn.roomNumber || '',
                contractorInfo: { name: '', phoneNumber: '', organization: '' },
                occupancyPeriod: { startDate: new Date(), endDate: new Date() },
                signatureName: '',
	              signatureDate: null,
	              contractSignedAt: null,
	              roomId: latestDrawn.roomId,
	              lotteryDocId: lotteryDocs[0]?.id,
	              cancellationRequestStatus: latestDrawn.cancellationRequestStatus,
	              cancellationReason: latestDrawn.cancellationReason,
	              canRequestCancellation: latestDrawn.cancellationRequestStatus !== 'pending'
	            });
	            return;
	          }
          }

          // 새 회차가 없으면 “현재 진행 없음” + 과거 계약 이력만 표시
          setContractInfo({
            ...(computedHistoryContractInfo || {
              roomNumber: '',
              contractorInfo: { name: '', phoneNumber: '', organization: '' },
              occupancyPeriod: { startDate: new Date(), endDate: new Date() },
              signatureName: '',
              signatureDate: null,
              contractSignedAt: null
            }),
            status: 'history'
          });
          return;
        }
      }

      // 2. 추첨 기록 확인 (residencies가 없을 때만: 레거시/진행중 단계 보조)
      const lotteryRef = collection(db, 'portville_lottery_history');
      const lotteryQuery = query(lotteryRef, where('uid', '==', user.uid));
      const lotterySnapshot = await getDocs(lotteryQuery);
      debugLog('lottery snapshot', { size: lotterySnapshot.size, fromCache: lotterySnapshot.metadata.fromCache });

      if (lotterySnapshot.empty) {
        // 추첨 기록도 없고, residencies도 없는 경우
        setContractInfo({
          status: 'none',
          roomNumber: '',
          contractorInfo: { name: '', phoneNumber: '', organization: '' },
          occupancyPeriod: { startDate: new Date(), endDate: new Date() },
          signatureName: '',
          signatureDate: null,
          contractSignedAt: null
        });
        return;
      }

      // NOTE: 쿼리 결과 순서는 보장되지 않으므로, 날짜 기준으로 최신 문서를 선택한다.
      const lotteryDocs = lotterySnapshot.docs
        .map((d) => ({ id: d.id, data: d.data() as any }))
        .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

      const lotteryData = lotteryDocs[0]?.data ?? null;
      debugLog('selected lottery', {
        id: lotteryDocs[0]?.id,
        status: lotteryData?.status,
        roomId: lotteryData?.roomId,
        roomNumber: lotteryData?.roomNumber,
        drawnAtMillis: getMillis(lotteryData?.drawnAt),
        archivedAtMillis: getMillis(lotteryData?.archivedAt),
        suppressedAtMillis: getMillis(lotteryData?.suppressedAt)
      });
      const roomId = lotteryData?.roomId;
      const lotteryStatus = lotteryData?.status;
      const lotteryDocuments = (lotteryData?.documents || {}) as Record<string, string | undefined>;
      const hasRequiredDocuments = Boolean(lotteryDocuments.identification && lotteryDocuments.employment);

      if (!roomId || !lotteryStatus) {
        setContractInfo({
          status: 'none',
          roomNumber: '',
          contractorInfo: { name: '', phoneNumber: '', organization: '' },
          occupancyPeriod: { startDate: new Date(), endDate: new Date() },
          signatureName: '',
          signatureDate: null,
          contractSignedAt: null
        });
        return;
      }

      if (lotteryStatus === 'drawn' && (lotteryData?.archivedAt || lotteryData?.suppressedAt)) {
        setContractInfo({
          status: 'none',
          roomNumber: '',
          contractorInfo: { name: '', phoneNumber: '', organization: '' },
          occupancyPeriod: { startDate: new Date(), endDate: new Date() },
          signatureName: '',
          signatureDate: null,
          contractSignedAt: null
        });
        return;
      }

      if (lotteryStatus === 'drawn' && !hasRequiredDocuments) {
        setNeedsDocuments(true);
        setContractInfo({
          status: 'drawn',
          roomNumber: lotteryData.roomNumber || '',
          contractorInfo: { name: '', phoneNumber: '', organization: '' },
          occupancyPeriod: { startDate: new Date(), endDate: new Date() },
          signatureName: '',
	          signatureDate: null,
	          contractSignedAt: null,
	          roomId,
	          lotteryDocId: lotteryDocs[0]?.id,
	          cancellationRequestStatus: lotteryData.cancellationRequestStatus,
	          cancellationReason: lotteryData.cancellationReason,
	          canRequestCancellation: lotteryData.cancellationRequestStatus !== 'pending'
	        });
	        return;
	      }

      if (lotteryStatus === 'drawn') {
        setContractInfo({
          status: 'drawn',
          roomNumber: lotteryData.roomNumber || '',
          contractorInfo: { name: '', phoneNumber: '', organization: '' },
          occupancyPeriod: { startDate: new Date(), endDate: new Date() },
          signatureName: '',
	          signatureDate: null,
	          contractSignedAt: null,
	          roomId,
	          lotteryDocId: lotteryDocs[0]?.id,
	          cancellationRequestStatus: lotteryData.cancellationRequestStatus,
	          cancellationReason: lotteryData.cancellationReason,
	          canRequestCancellation: lotteryData.cancellationRequestStatus !== 'pending'
	        });
	        return;
	      }

      if (lotteryStatus === 'contracted') {
        const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
        if (roomDoc.exists()) {
          const roomData = roomDoc.data();
          setContractInfo({
            status: 'contracted',
            roomNumber: roomData.roomNumber,
            contractorInfo: {
              name: roomData.currentResident?.name || '',
              phoneNumber: roomData.currentResident?.phoneNumber || '',
              organization: roomData.currentResident?.organization || ''
            },
            occupancyPeriod: {
              startDate: roomData.occupancyPeriod?.startDate?.toDate() || new Date(),
              endDate: roomData.occupancyPeriod?.endDate?.toDate() || new Date()
            },
            signatureName: roomData.signatureName || '',
            signatureDate: roomData.signatureDate?.toDate?.() || null,
            contractSignedAt: roomData.contractSignedAt?.toDate?.() || null,
            roomId
          });
          return;
        }
      }
      
    } catch (error) {
      console.error('Error loading contract info:', error);
      setError('계약 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadContractContent = async () => {
    if (!user || !contractInfo?.roomId) return;
    
    try {
      setContractLoading(true);
      debugLog('loadContractContent start', { uid: user.uid, roomId: contractInfo.roomId, at: new Date().toISOString() });
      
      // 1. 먼저 새로운 데이터 구조에서 입주 기록 확인 (보안 검증)
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
      const residencySnapshot = await getDocs(residencyQuery);
      debugLog('loadContractContent residencies snapshot', {
        size: residencySnapshot.size,
        fromCache: residencySnapshot.metadata.fromCache
      });
      
      let hasPermission = false;
      const roomIdKey = String(contractInfo.roomId);
      const matched = !residencySnapshot.empty
        ? residencySnapshot.docs.find((snap) => String(snap.data().roomId) === roomIdKey) ?? residencySnapshot.docs[0]
        : null;
      const residencyData = matched ? matched.data() : null;
      debugLog('loadContractContent residency match', {
        matchedId: matched?.id,
        matchedFromCache: matched?.metadata.fromCache,
        matchedRoomId: residencyData?.roomId,
        matchedStatus: residencyData?.status,
        hasContractContent: Boolean(residencyData?.contractContent)
      });
      
      if (residencyData) {
        // 새로운 데이터 구조에서 권한 확인
        if (residencyData.residentUid === user.uid && residencyData.roomId === contractInfo.roomId) {
          hasPermission = true;
        }
      } else {
        // 기존 데이터 구조 호환성 확인
        const roomDoc = await getDoc(doc(db, 'portville_rooms', contractInfo.roomId));
        if (roomDoc.exists()) {
          const roomData = roomDoc.data();
          if (roomData.contractorUid === user.uid) {
            hasPermission = true;
          }
        }
      }
      
      if (!hasPermission) {
        throw new Error('계약서 열람 권한이 없습니다.');
      }
      
      // 2. 새로운 데이터 구조에서 입주 기록에 계약 내용이 있는지 먼저 확인
      if (residencyData?.contractContent) {
        debugLog('loadContractContent using residencies.contractContent');
        setContractContent({
          article1: residencyData.contractContent?.article1 || '',
          article2: residencyData.contractContent?.article2 || '',
          article3: residencyData.contractContent?.article3 || '',
          article4: residencyData.contractContent?.article4 || '',
          article5: residencyData.contractContent?.article5 || '',
          signatureUrl: residencyData.signatureUrl || ''
        });
        
        setShowContractModal(true);
        return;
      }
      debugLog('loadContractContent no contractContent on residency; blocking fallback');
      throw new Error('계약서 내용이 등록되어 있지 않습니다. 관리자에게 문의하세요.');
      
    } catch (error) {
      console.error('Error loading contract content:', error);
      alert(error instanceof Error ? error.message : '계약서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setContractLoading(false);
    }
  };

  const closeContractModal = () => {
    setShowContractModal(false);
    setContractContent(null);
  };

  const handleRequestCancellation = async () => {
    if (!contractInfo?.lotteryDocId && !contractInfo?.roomId) {
      setCancellationError('추첨 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (contractInfo?.cancellationRequestStatus === 'pending') {
      setCancellationError('이미 계약 포기 신청이 접수되어 관리자 검토 중입니다.');
      return;
    }

    if (!contractInfo?.canRequestCancellation) {
      setCancellationError('현재 회차의 추첨 정보를 확인할 수 없어 계약 포기를 신청할 수 없습니다. 관리자에게 문의해주세요.');
      return;
    }

    const reason = window.prompt('계약 포기 신청 사유를 입력해주세요. 관리자 승인 후 최종 포기 처리됩니다.');
    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setCancellationError('계약 포기 신청 사유를 입력해주세요.');
      return;
    }

    const confirmed = window.confirm(
      `${contractInfo.roomNumber}호 계약 포기 신청을 제출하시겠습니까?\n\n제출 후 관리자 승인 전까지 계약 진행이 제한됩니다.`
    );
    if (!confirmed) return;

    try {
      setIsRequestingCancellation(true);
      setCancellationError(null);
      await requestPortvilleContractCancellation({
        lotteryId: contractInfo.lotteryDocId,
        roomId: contractInfo.roomId,
        cancellationReason: trimmedReason
      });

      setContractInfo((prev) => prev ? {
        ...prev,
        cancellationRequestStatus: 'pending',
        cancellationReason: trimmedReason,
        canRequestCancellation: false
      } : prev);
      alert('계약 포기 신청이 접수되었습니다. 관리자 검토 후 최종 처리됩니다.');
    } catch (error: any) {
      console.error('계약 포기 신청 실패:', error);
      const message = error?.message || '계약 포기 신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      setCancellationError(message);
    } finally {
      setIsRequestingCancellation(false);
    }
  };

  if (authLoading || emailVerificationLoading) {
      return (
        <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  const hasCancellationPending = contractInfo?.cancellationRequestStatus === 'pending';
  const hasCancellationRejected = contractInfo?.cancellationRequestStatus === 'rejected';
  const canRequestCancellation = Boolean(contractInfo?.canRequestCancellation && !hasCancellationPending);

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
          <div className="w-full max-w-[750px] px-4 sm:px-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
              포트빌 입주 계약
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-normal text-slate-700 font-['Pretendard'] leading-9 mb-8">
              배정받은 호실의 입주 계약을 확인해주세요
            </p>
            <div className="w-full h-px bg-gray-200 mb-8"></div>

            {needsDocuments ? (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <p className="text-yellow-900 text-center font-bold mb-2">
                    계약 진행 전, 필수 서류 업로드가 필요합니다.
                  </p>
                  {contractInfo?.roomNumber && (
                    <p className="text-yellow-800 text-center mb-4">
                      배정 호실: <span className="font-bold">{contractInfo.roomNumber}호</span>
                    </p>
                  )}
	                  <p className="text-yellow-800 text-center text-sm mb-6">
	                    {hasCancellationPending
	                      ? '계약 포기 신청이 접수되어 관리자 검토 중입니다.'
	                      : '호실 추첨 페이지에서 신분증 사본 및 재직증명서/사업자등록증을 업로드한 뒤 계약을 진행해주세요.'}
	                  </p>
	                  {hasCancellationPending && (
	                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
	                      관리자 승인 전까지 계약 진행이 제한됩니다.
	                      {contractInfo?.cancellationReason && (
	                        <div className="mt-1">신청 사유: {contractInfo.cancellationReason}</div>
	                      )}
	                    </div>
	                  )}
	                  {hasCancellationRejected && (
	                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
	                      이전 계약 포기 신청이 반려되었습니다. 필요 시 다시 신청할 수 있습니다.
	                    </div>
	                  )}
	                  {cancellationError && (
	                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
	                      {cancellationError}
	                    </div>
	                  )}
	                  <div className="flex flex-col gap-3">
	                    <button
	                      onClick={() => navigate('/portville/lottery')}
	                      disabled={hasCancellationPending}
	                      className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
	                    >
	                      서류 업로드 하러 가기
	                    </button>
	                    <button
	                      onClick={handleRequestCancellation}
	                      disabled={isRequestingCancellation || !canRequestCancellation}
	                      className={`w-full h-14 sm:h-16 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9 ${
	                        isRequestingCancellation || !canRequestCancellation
	                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
	                          : 'bg-white text-red-700 border border-red-200 hover:bg-red-50 focus:ring-red-500'
	                      }`}
	                    >
	                      {hasCancellationPending
	                        ? '계약 포기 신청 검토 중'
	                        : isRequestingCancellation
	                          ? '계약 포기 신청 중...'
	                          : hasCancellationRejected
	                            ? '계약 포기 재신청'
	                          : '계약 포기 신청'}
	                    </button>
	                    <button
	                      onClick={() => navigate('/portville')}
	                      className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      포트빌 메인으로 돌아가기
                    </button>
                  </div>
                </div>
                {historyContractInfo && renderHistorySummary(historyContractInfo)}
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">{error}</p>
              </div>
            ) : contractInfo?.status === 'history' ? (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded border border-black/5 p-8 sm:p-12">
                  <div className="text-center">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Pretendard'] leading-9 mb-4">
                      현재 진행 중인 계약이 없습니다
                    </h2>
                    <p className="text-base text-slate-600 font-['Pretendard'] mb-6 leading-relaxed">
                      진행 중인 추첨/계약 내역이 확인되지 않습니다.
                      <br />
                      포트빌 메인에서 신청/대기 현황을 확인해주세요.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => navigate('/portville')}
                        className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        포트빌 메인으로 이동
                      </button>
                      <button
                        onClick={() => navigate('/portville/lottery')}
                        className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        호실 추첨 페이지로 이동
                      </button>
                    </div>
                  </div>
                </div>
                {historyContractInfo && renderHistorySummary(historyContractInfo)}
              </div>
            ) : contractInfo?.status === 'none' ? (
              // 추첨 기록이 없는 경우
            <div className="bg-gray-50 rounded border border-black/5 p-8 sm:p-12">
              <div className="text-center">
                <div className="mb-6">
                  <svg 
                    className="w-16 h-16 text-gray-400 mx-auto mb-4" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                        d="M9 12L11 14L15 10" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <circle 
                      cx="12" 
                      cy="12" 
                      r="9" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Pretendard'] leading-9 mb-4">
                    호실 추첨을 먼저 진행해주세요
                </h2>
                
                <p className="text-base text-slate-600 font-['Pretendard'] mb-8 leading-relaxed">
                    아직 호실 추첨을 진행하지 않으셨습니다.<br />
                    먼저 호실 추첨을 통해 호실을 배정받으신 후<br />
                    계약을 진행할 수 있습니다.
                  </p>
                </div>
              </div>
            ) : contractInfo?.status === 'drawn' ? (
              // 추첨 완료, 계약 대기 상태
              <div className="space-y-6">
                <div className="bg-yellow-50 rounded border border-yellow-200 p-8 sm:p-12">
                  <div className="text-center">
                    <div className="mb-6">
                      <svg
                        className="w-16 h-16 text-yellow-500 mx-auto mb-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 8V12L15 15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>

                    <h2 className="text-xl sm:text-2xl font-bold text-yellow-800 font-['Pretendard'] leading-9 mb-4">
                      계약 날인 대기중
                    </h2>

                    <p className="text-base text-yellow-700 font-['Pretendard'] mb-6 leading-relaxed">
                      <span className="font-bold text-lg">{contractInfo.roomNumber}호</span>가 배정되었습니다.<br />
                      신항시설관리센터를 방문하시어<br />
                      입주 사전점검 및 계약 날인을 진행해주세요.
                    </p>

	                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
	                      <p className="text-yellow-800 text-sm font-['Pretendard']">
	                        💡 계약 및 입주 관련 문의사항이 있으시면 신항시설관리센터로 연락해 주세요.<br />
	                        051-890-7455
	                      </p>
	                    </div>
	                    {hasCancellationPending && (
	                      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
	                        <p className="text-red-800 text-sm font-bold font-['Pretendard']">
	                          계약 포기 신청 검토 중
	                        </p>
	                        <p className="text-red-700 text-sm font-['Pretendard'] mt-1">
	                          관리자 승인 전까지 계약 진행이 제한됩니다.
	                        </p>
	                        {contractInfo.cancellationReason && (
	                          <p className="text-red-700 text-sm font-['Pretendard'] mt-1">
	                            신청 사유: {contractInfo.cancellationReason}
	                          </p>
	                        )}
	                      </div>
	                    )}
	                    {hasCancellationRejected && (
	                      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
	                        <p className="text-yellow-900 text-sm font-bold font-['Pretendard']">
	                          이전 계약 포기 신청 반려
	                        </p>
	                        <p className="text-yellow-800 text-sm font-['Pretendard'] mt-1">
	                          필요 시 계약 포기 신청을 다시 제출할 수 있습니다.
	                        </p>
	                      </div>
	                    )}
	                    {cancellationError && (
	                      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
	                        <p className="text-red-800 text-sm font-['Pretendard']">{cancellationError}</p>
	                      </div>
	                    )}
	                    <button
	                      onClick={handleRequestCancellation}
	                      disabled={isRequestingCancellation || !canRequestCancellation}
	                      className={`mt-4 w-full h-14 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-base font-bold font-['Pretendard'] ${
	                        isRequestingCancellation || !canRequestCancellation
	                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
	                          : 'bg-white text-red-700 border border-red-200 hover:bg-red-50 focus:ring-red-500'
	                      }`}
	                    >
	                      {hasCancellationPending
	                        ? '계약 포기 신청 검토 중'
	                        : isRequestingCancellation
	                          ? '계약 포기 신청 중...'
	                          : hasCancellationRejected
	                            ? '계약 포기 재신청'
		                          : canRequestCancellation
		                            ? '계약 포기 신청'
		                            : '계약 포기 신청 불가'}
	                    </button>
	                  </div>
	                </div>

                {historyContractInfo && renderHistorySummary(historyContractInfo)}
              </div>
            ) : contractInfo?.status === 'contracted' ? (
              // 계약 완료 상태
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center justify-center mb-4">
                    <svg 
                      className="w-12 h-12 text-green-500" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M9 12L11 14L15 10" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                      <circle 
                        cx="12" 
                        cy="12" 
                        r="9" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-green-800 text-center mb-2">
                    계약이 완료되었습니다!
                  </h2>
                  <p className="text-green-700 text-center">
                    포트빌 <span className="font-bold">{contractInfo.roomNumber}호</span> 입주 계약
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">계약 정보</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">호실번호</dt>
                      <dd className="text-base font-semibold text-gray-900">{contractInfo.roomNumber}호</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">계약자</dt>
                      <dd className="text-base font-semibold text-gray-900">{contractInfo.contractorInfo.name}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">연락처</dt>
                      <dd className="text-base font-semibold text-gray-900">{contractInfo.contractorInfo.phoneNumber}</dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">소속</dt>
                      <dd className="text-base font-semibold text-gray-900">{contractInfo.contractorInfo.organization}</dd>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">입주 기간</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">입주 시작일</dt>
                      <dd className="text-base font-semibold text-gray-900">
                        {contractInfo.occupancyPeriod.startDate.toLocaleDateString('ko-KR')}
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">계약 종료일</dt>
                      <dd className="text-base font-semibold text-gray-900">
                        {contractInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}
                      </dd>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      📅 계약 기간: 2년 ({contractInfo.occupancyPeriod.startDate.toLocaleDateString('ko-KR')} ~ {contractInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')})
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">계약 날인 정보</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">서명자</dt>
                      <dd className="text-base font-semibold text-gray-900">{contractInfo.signatureName}</dd>
            </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500">날인 완료일</dt>
                      <dd className="text-base font-semibold text-gray-900">
                        {contractInfo.contractSignedAt ? contractInfo.contractSignedAt.toLocaleDateString('ko-KR') : '-'}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* 계약서 보기 버튼 추가 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-blue-900 mb-2">계약서 열람</h3>
                      <p className="text-sm text-blue-700">
                        체결된 계약서의 전체 내용을 확인하실 수 있습니다.
                      </p>
                    </div>
                    <button
                      onClick={loadContractContent}
                      disabled={contractLoading}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {contractLoading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          로딩중...
                        </div>
                      ) : (
                        '계약서 보기'
                      )}
                    </button>
                  </div>
                </div>

                {historyContractInfo &&
                  historyContractInfo.roomId &&
                  historyContractInfo.roomId !== contractInfo.roomId &&
                  renderHistorySummary(historyContractInfo)}
              </div>
            ) : null}

            <div className="mt-8 space-y-4">
              {contractInfo?.status === 'none' && (
              <button
                onClick={() => navigate('/portville/lottery')}
                className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                  호실 추첨하기
              </button>
              )}
              <button
                onClick={() => navigate('/portville')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                포트빌 메인으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 계약서 보기 모달 */}
      {showContractModal && contractContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">포트빌 입주 계약서</h2>
              <button
                onClick={closeContractModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">포트빌 입주 계약서</h1>
                <p className="text-gray-600">호실번호: {contractInfo?.roomNumber}호</p>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제1조 (계약 당사자)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article1}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제2조 (임대차 목적물)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article2}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제3조 (계약 기간)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article3}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제4조 (임차인의 의무)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article4}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제5조 (계약의 효력)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article5}</p>
                </div>

                {contractContent.signatureUrl && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-4">계약자 서명</h3>
                    <div className="flex flex-col items-center space-y-4">
                      <img 
                        src={contractContent.signatureUrl} 
                        alt="계약자 서명" 
                        className="max-w-xs border border-gray-300 rounded"
                      />
                      <div className="text-center">
                        <p className="text-sm text-gray-600">서명자: {contractInfo?.signatureName}</p>
                        <p className="text-sm text-gray-600">
                          서명일: {contractInfo?.signatureDate ? contractInfo.signatureDate.toLocaleDateString('ko-KR') : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4 text-center text-sm text-gray-500">
                <p>
                  본 계약서는 {contractInfo?.contractSignedAt ? contractInfo.contractSignedAt.toLocaleDateString('ko-KR') : '-'}에
                  체결되었습니다.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={closeContractModal}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </ClientLayout>
  );
};

export default ContractPage; 
